import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Calendar, Plus, Edit, Trash2, Eye, EyeOff, Users,
  MapPin, Clock, DollarSign, ArrowLeft, Search,
  Filter, MoreVertical, CheckCircle, XCircle, Loader2, RefreshCw,
  Sparkles, Upload, Image as ImageIcon, X as XIcon, Copy
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { sampleEventsForAdmin } from "../data/sampleEvents";
import { toast } from "sonner";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusLabels = {
  upcoming: { label: "예정", color: "bg-blue-100 text-blue-600" },
  full: { label: "마감", color: "bg-red-100 text-red-600" },
  ongoing: { label: "진행중", color: "bg-green-100 text-green-600" },
  completed: { label: "종료", color: "bg-stone-100 text-stone-600" },
};

const typeLabels = {
  offline: { label: "오프라인", color: "bg-purple-100 text-purple-600" },
  online: { label: "온라인", color: "bg-cyan-100 text-cyan-600" },
};

export default function AdminEventsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading, token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  
  // 공지 카테고리 옵션 - 메뉴의 모든 카테고리
  const noticeCategories = [
    // 기본
    { id: "all", label: "전체 공지", description: "모든 회원에게 공지", group: "기본" },
    
    // 회원 타겟
    { id: "40s", label: "40대 회원", description: "40대 회원에게만", group: "회원 타겟" },
    { id: "50s", label: "50대 회원", description: "50대 회원에게만", group: "회원 타겟" },
    { id: "60s", label: "60대 회원", description: "60대 회원에게만", group: "회원 타겟" },
    { id: "premium", label: "프리미엄 이상", description: "프리미엄/VIP 회원만", group: "회원 타겟" },
    
    // 커뮤니티
    { id: "community_notice", label: "커뮤니티 공지", description: "공지 게시판", group: "커뮤니티" },
    { id: "community_welcome", label: "가입인사", description: "가입인사 게시판", group: "커뮤니티" },
    { id: "community_love", label: "연애와 썸", description: "연애와 썸 게시판", group: "커뮤니티" },
    { id: "community_breakup", label: "이별&상처", description: "이별&상처 게시판", group: "커뮤니티" },
    { id: "community_daily", label: "일상&취미", description: "일상&취미 게시판", group: "커뮤니티" },
    { id: "community_advice", label: "고민상담", description: "고민상담 게시판", group: "커뮤니티" },
    { id: "community_self", label: "자기관리&매력", description: "자기관리&매력 게시판", group: "커뮤니티" },
    { id: "community_friends", label: "친구&모임", description: "친구&모임 게시판", group: "커뮤니티" },
    { id: "community_life", label: "인생2막 노후계획", description: "노후계획 게시판", group: "커뮤니티" },
    { id: "community_health", label: "건강&뷰티", description: "건강&뷰티 게시판", group: "커뮤니티" },
    { id: "community_global", label: "글로벌 싱글라이프", description: "글로벌 게시판", group: "커뮤니티" },
    { id: "community_date", label: "데이트 코스추천", description: "데이트 코스 게시판", group: "커뮤니티" },
    { id: "community_review", label: "가입후기", description: "가입후기 게시판", group: "커뮤니티" },
    
    // NC커플즈
    { id: "couple_success", label: "성공스토리", description: "커플 성공스토리", group: "NC커플즈" },
    { id: "couple_date_review", label: "데이트 후기", description: "커플 데이트 후기", group: "NC커플즈" },
    { id: "couple_event", label: "커플 이벤트", description: "커플 이벤트 게시판", group: "NC커플즈" },
    { id: "couple_chat", label: "커플 채팅방", description: "커플 채팅방", group: "NC커플즈" },
    
    // 매칭
    { id: "matching_dating_app", label: "데이팅 앱", description: "데이팅 앱 사용자", group: "매칭" },
    { id: "matching_zoom", label: "줌미팅", description: "줌미팅 참여자", group: "매칭" },
    { id: "matching_group", label: "소그룹 오프미팅", description: "소그룹 오프미팅", group: "매칭" },
    { id: "matching_project", label: "연인만들기 프로젝트", description: "연인만들기 참여자", group: "매칭" },
    
    // 이벤트
    { id: "event_offline_4050", label: "오프모임 4050", description: "4050 오프모임 참가자", group: "이벤트" },
    { id: "event_offline_5060", label: "오프모임 5060", description: "5060 오프모임 참가자", group: "이벤트" },
    { id: "event_small_hobby", label: "소모임 취미별", description: "취미별 소모임 참가자", group: "이벤트" },
    { id: "event_small_region", label: "소모임 지역별", description: "지역별 소모임 참가자", group: "이벤트" },
    { id: "event_party", label: "파티", description: "파티 이벤트 참가자", group: "이벤트" },
    { id: "event_camp", label: "캠프", description: "캠프 참가자", group: "이벤트" },
    
    // 회원정보
    { id: "members_korea", label: "한국 회원", description: "한국 거주 회원", group: "회원정보" },
    { id: "members_usa", label: "미국 회원", description: "미국 거주 회원", group: "회원정보" },
    { id: "members_canada", label: "캐나다 회원", description: "캐나다 거주 회원", group: "회원정보" },
    { id: "members_australia", label: "호주 회원", description: "호주 거주 회원", group: "회원정보" },
  ];
  
  // 카테고리 그룹 목록
  const categoryGroups = [...new Set(noticeCategories.map(c => c.group))];

  // 새 이벤트 폼 상태
  const [newEvent, setNewEvent] = useState({
    title: "",
    type: "offline",
    date: "",
    endDate: "",
    time: "",
    location: "",
    price: "",
    currency: "KRW",
    maxParticipants: "",
    description: "",
    noticeCategories: ["all"],
    image: null,
    timezone: "Asia/Seoul",
    eventCategory: "offline",
    eligibility: "all",
  });

  // 이미지 작업 상태
  const [imageBusy, setImageBusy] = useState(false);
  const [imageMode, setImageMode] = useState("ai"); // "ai" | "upload"
  const [aiPrompt, setAiPrompt] = useState("");

  // 타임존 옵션
  const timezoneOptions = [
    { value: "Asia/Seoul", label: "한국 시간 (KST)" },
    { value: "America/New_York", label: "미국 동부 (ET)" },
    { value: "America/Los_Angeles", label: "미국 서부 (PT)" },
  ];

  // 통화 옵션
  const currencyOptions = [
    { value: "KRW", label: "원화 (₩ KRW)", symbol: "₩" },
    { value: "USD", label: "미국 달러 ($ USD)", symbol: "$" },
  ];

  // 이벤트 분류 옵션 (공개 이벤트 페이지 필터링과 매핑)
  const eventCategoryOptions = [
    { value: "offline", label: "매칭&디너", emoji: "🍷", desc: "오프라인 매칭/디너 이벤트" },
    { value: "small", label: "소모임", emoji: "💜", desc: "취미·지역 기반 소모임" },
    { value: "party", label: "파티", emoji: "🎉", desc: "송년·시즌 파티 이벤트" },
    { value: "camp", label: "캠프", emoji: "⛺", desc: "1박 이상 캠프·여행" },
  ];

  // 참여 대상(회원 등급) 옵션
  const eligibilityOptions = [
    { value: "all", label: "전체 회원", desc: "누구나 참여 가능" },
    { value: "regular", label: "정회원 전용", desc: "정회원 이상만 참여" },
    { value: "vip", label: "VIP 전용", desc: "VIP 회원만 참여" },
  ];
  const eligibilityLabel = (v) => (eligibilityOptions.find((o) => o.value === v)?.label || "전체 회원");

  // 통화 기호 헬퍼
  const currencySymbol = (code) => (code === "USD" ? "$" : "₩");
  const formatPrice = (price, currency) => {
    const sym = currencySymbol(currency);
    const value = (price || 0).toLocaleString();
    return currency === "USD" ? `${sym}${value}` : `${value}${sym === "₩" ? "원" : ""}`;
  };

  // 이벤트 목록 불러오기
  const fetchEvents = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await safeFetch(`${API_URL}/api/admin/events`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch events');
      
      const data = await response.json();
      setEvents(data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 숨김 처리된 샘플 이벤트 ID
  const [hiddenSampleIds, setHiddenSampleIds] = useState([]);
  const fetchHiddenSampleIds = useCallback(async () => {
    try {
      const response = await safeFetch(`${API_URL}/api/sample-events/hidden`);
      if (response.ok) {
        const data = await response.json();
        setHiddenSampleIds(data.hidden_ids || []);
      }
    } catch (e) {
      console.error("Failed to fetch hidden sample ids", e);
    }
  }, []);
  const [showHiddenSamples, setShowHiddenSamples] = useState(false);

  const handleToggleSampleHidden = async (id, hidden) => {
    try {
      const response = await safeFetch(`${API_URL}/api/admin/sample-events/toggle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id, hidden }),
      });
      if (!response.ok) throw new Error("toggle failed");
      const data = await response.json();
      setHiddenSampleIds(data.hidden_ids || []);
    } catch (e) {
      console.error("Failed to toggle sample", e);
      alert("샘플 이벤트 상태 변경에 실패했습니다.");
    }
  };

  // 인라인 — 참가/모집 인원 즉시 저장
  const [participantEdits, setParticipantEdits] = useState({});

  const handleInlineParticipantsSave = async (event, field, value) => {
    if (event.isSample) return; // 샘플은 DB 없음
    const numericValue = Math.max(0, parseInt(value, 10) || 0);
    if (numericValue === (event[field] ?? 0)) {
      // 변경 없음 → 임시 편집 상태만 삭제
      setParticipantEdits((prev) => {
        const next = { ...prev };
        if (next[event.id]) {
          delete next[event.id][field];
          if (Object.keys(next[event.id]).length === 0) delete next[event.id];
        }
        return next;
      });
      return;
    }
    try {
      const response = await safeFetch(`${API_URL}/api/admin/events/${event.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ [field]: numericValue }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "저장 실패");
      }
      // 편집 상태 클리어 후 새 데이터 fetch
      setParticipantEdits((prev) => {
        const next = { ...prev };
        if (next[event.id]) {
          delete next[event.id][field];
          if (Object.keys(next[event.id]).length === 0) delete next[event.id];
        }
        return next;
      });
      await fetchEvents();
    } catch (e) {
      console.error("inline save failed", e);
      alert(e.message || "참가자 수 저장에 실패했습니다.");
    }
  };

  // 샘플 이벤트를 실제 DB 이벤트로 복제(가져오기)
  const handleCloneSampleToDB = async (sample) => {
    if (!sample) return;
    const ok = window.confirm(
      `"${sample.title}"을(를) 실제 이벤트로 가져오시겠어요?\n\n` +
      `복제 후에는 일반 이벤트처럼 수정/삭제할 수 있고, 신청도 받을 수 있습니다. ` +
      `원본 샘플 카드는 그대로 유지됩니다(필요 시 숨김 처리 가능).`
    );
    if (!ok) return;
    try {
      const response = await safeFetch(`${API_URL}/api/admin/events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: sample.title,
          type: sample.type || "offline",
          date: sample.date,
          time: sample.time,
          location: sample.location,
          price: parseInt(sample.price) || 0,
          currency: sample.currency || "KRW",
          maxParticipants: parseInt(sample.maxParticipants) || 20,
          description: sample.description || "",
          noticeCategories: sample.noticeCategories || ["all"],
          image: sample.image || null,
          timezone: sample.timezone || "Asia/Seoul",
          eventCategory: sample.eventCategory || "offline",
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || "복제 실패");
      }
      await fetchEvents();
      alert("샘플이 실제 이벤트로 복제되었습니다. 목록 상단에서 확인하실 수 있어요.");
    } catch (e) {
      console.error("Clone failed", e);
      alert(e.message || "복제에 실패했습니다.");
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchEvents();
    fetchHiddenSampleIds();
  }, [isAuthenticated, user, authLoading, navigate, fetchEvents, fetchHiddenSampleIds]);

  // 날짜가 지난 이벤트는 자동으로 "종료" 상태로 표기
  const getEffectiveStatus = (event) => {
    if (!event?.date) return event?.status || "upcoming";
    try {
      const eventDateTime = new Date(`${event.date}T${event.time || "23:59"}:00`);
      if (!isNaN(eventDateTime.getTime()) && eventDateTime.getTime() < Date.now()) {
        return "completed";
      }
    } catch (e) {
      // ignore parse errors and fall through
    }
    return event.status || "upcoming";
  };

  const eventsWithEffectiveStatus = [...events, ...sampleEventsForAdmin].map(e => ({
    ...e,
    effectiveStatus: getEffectiveStatus(e),
    isHidden: e.isSample && hiddenSampleIds.includes(e.id),
  }));

  const filteredEvents = eventsWithEffectiveStatus
    .filter(event => {
      const matchesSearch = event.title?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === "all" || event.effectiveStatus === filterStatus;
      const matchesHidden = showHiddenSamples || !event.isHidden;
      return matchesSearch && matchesStatus && matchesHidden;
    })
    .sort((a, b) => {
      // 날짜+시간 기준 최신순 (descending)
      const aTime = new Date(`${a.date || "1970-01-01"}T${a.time || "00:00"}:00`).getTime();
      const bTime = new Date(`${b.date || "1970-01-01"}T${b.time || "00:00"}:00`).getTime();
      return bTime - aTime;
    });

  // 이벤트 생성
  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.date || !newEvent.time || !newEvent.location) {
      alert("필수 항목을 모두 입력해주세요.");
      return;
    }

    if (!newEvent.noticeCategories || newEvent.noticeCategories.length === 0) {
      alert("최소 1개 이상의 공지 카테고리를 선택해주세요.");
      return;
    }

    try {
      setSubmitting(true);
      const response = await safeFetch(`${API_URL}/api/admin/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: newEvent.title,
          type: newEvent.type,
          date: newEvent.date,
          endDate: newEvent.endDate || null,
          time: newEvent.time,
          location: newEvent.location,
          price: parseInt(newEvent.price) || 0,
          currency: newEvent.currency || "KRW",
          maxParticipants: parseInt(newEvent.maxParticipants) || 20,
          description: newEvent.description || "",
          noticeCategories: newEvent.noticeCategories,
          image: newEvent.image || null,
          timezone: newEvent.timezone || "Asia/Seoul",
          eventCategory: newEvent.eventCategory || "offline",
          eligibility: newEvent.eligibility || "all",
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create event');
      }

      const data = await response.json();
      
      setShowCreateModal(false);
      setNewEvent({
        title: "",
        type: "offline",
        date: "",
        endDate: "",
        time: "",
        location: "",
        price: "",
        currency: "KRW",
        maxParticipants: "",
        description: "",
        noticeCategories: ["all"],
        image: null,
        timezone: "Asia/Seoul",
        eventCategory: "offline",
        eligibility: "all",
      });
      setImageMode("ai");
      
      alert(data.message || "이벤트가 생성되었습니다!");
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      alert(error.message || "이벤트 생성에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  // 이벤트 삭제
  const handleDelete = async (id) => {
    if (!confirm("정말 이 이벤트를 삭제하시겠습니까?")) return;

    try {
      const response = await safeFetch(`${API_URL}/api/admin/events/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete event');

      alert("이벤트가 삭제되었습니다.");
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert("이벤트 삭제에 실패했습니다.");
    }
  };

  // 이벤트 수정 모달 열기
  const handleEditClick = (event) => {
    setEditingEvent({
      ...event,
      endDate: event.endDate || "",
      price: event.price?.toString() || "",
      currency: event.currency || "KRW",
      maxParticipants: event.maxParticipants?.toString() || "20",
      currentParticipants: (event.currentParticipants ?? 0).toString(),
      noticeCategories: event.noticeCategories || ["all"],
      timezone: event.timezone || "Asia/Seoul",
      eventCategory: event.eventCategory || "offline",
      eligibility: event.eligibility || "all",
    });
    setShowEditModal(true);
  };

  // 이벤트 수정
  const handleUpdateEvent = async () => {
    if (!editingEvent?.title || !editingEvent?.date || !editingEvent?.time || !editingEvent?.location) {
      alert("필수 항목을 모두 입력해주세요.");
      return;
    }

    try {
      setSubmitting(true);
      const response = await safeFetch(`${API_URL}/api/admin/events/${editingEvent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editingEvent.title,
          type: editingEvent.type,
          date: editingEvent.date,
          endDate: editingEvent.endDate || null,
          time: editingEvent.time,
          location: editingEvent.location,
          price: parseInt(editingEvent.price) || 0,
          currency: editingEvent.currency || "KRW",
          maxParticipants: parseInt(editingEvent.maxParticipants) || 20,
          currentParticipants: parseInt(editingEvent.currentParticipants) || 0,
          description: editingEvent.description || "",
          status: editingEvent.status,
          noticeCategories: editingEvent.noticeCategories,
          image: editingEvent.image || null,
          timezone: editingEvent.timezone || "Asia/Seoul",
          eventCategory: editingEvent.eventCategory || "offline",
          eligibility: editingEvent.eligibility || "all",
        })
      });

      if (!response.ok) throw new Error('Failed to update event');

      setShowEditModal(false);
      setEditingEvent(null);
      alert("이벤트가 수정되었습니다.");
      fetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      alert("이벤트 수정에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  // 이벤트 이미지 AI 자동 생성
  const handleGenerateImage = async (target, setTarget) => {
    if (!target.title && !aiPrompt.trim()) {
      alert("이벤트 제목을 입력하거나 직접 프롬프트를 작성해주세요.");
      return;
    }
    try {
      setImageBusy(true);
      const response = await safeFetch(`${API_URL}/api/admin/events/generate-image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: target.title,
          description: target.description || "",
          type: target.type || "offline",
          custom_prompt: aiPrompt.trim() || null,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "이미지 생성에 실패했습니다");
      }
      const data = await response.json();
      setTarget({ ...target, image: data.url });
    } catch (error) {
      console.error("AI image generation error:", error);
      alert(error.message || "AI 이미지 생성에 실패했습니다.");
    } finally {
      setImageBusy(false);
    }
  };

  // 이벤트 이미지 직접 업로드
  const handleUploadImage = async (file, target, setTarget) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("이미지 파일은 10MB 이하여야 합니다.");
      return;
    }
    try {
      setImageBusy(true);
      const formData = new FormData();
      formData.append("file", file);
      const response = await safeFetch(`${API_URL}/api/admin/events/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "업로드 실패");
      }
      const data = await response.json();
      setTarget({ ...target, image: data.url });
    } catch (error) {
      console.error("Image upload error:", error);
      alert(error.message || "이미지 업로드에 실패했습니다.");
    } finally {
      setImageBusy(false);
    }
  };

  // 이미지 URL을 전체 URL로 변환
  const fullImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${API_URL}${url}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 pt-20">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-stone-500 hover:text-burgundy-500 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            대시보드로 돌아가기
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-stone-800">이벤트 관리</h1>
                <p className="text-stone-500 text-sm">오프라인 미팅 및 줌 데이트 관리</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchEvents}
                className="border-stone-200"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                onClick={async () => {
                  if (!window.confirm("동일 제목의 이벤트 중복을 자동 정리합니다.\n각 제목당 가장 최근(최신순) 1건만 남기고 나머지는 영구 삭제됩니다.\n계속하시겠습니까?")) return;
                  try {
                    const res = await safeFetch(`${API_URL}/api/admin/events/dedupe`, {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    const d = await res.json();
                    if (!res.ok) throw new Error(d.detail || "실패");
                    toast.success(d.message || `${d.deleted}건 삭제 완료`);
                    fetchEvents();
                  } catch (e) {
                    toast.error(e.message || "정리 실패");
                  }
                }}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="dedupe-events-btn"
                title="동일 제목 이벤트 중복 자동 정리"
              >
                🧹 중복 정리
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-purple-500 hover:bg-purple-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                새 이벤트
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-stone-500 text-sm">전체 이벤트</p>
            <p className="text-2xl font-bold text-stone-800">{eventsWithEffectiveStatus.length}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-stone-500 text-sm">예정된 이벤트</p>
            <p className="text-2xl font-bold text-blue-600">
              {eventsWithEffectiveStatus.filter(e => e.effectiveStatus === "upcoming").length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-stone-500 text-sm">마감된 이벤트</p>
            <p className="text-2xl font-bold text-red-600">
              {eventsWithEffectiveStatus.filter(e => e.effectiveStatus === "full").length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <p className="text-stone-500 text-sm">총 참가자</p>
            <p className="text-2xl font-bold text-green-600">
              {eventsWithEffectiveStatus.reduce((sum, e) => sum + (e.currentParticipants || 0), 0)}명
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 mb-6 border border-stone-200">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                placeholder="이벤트 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
            >
              <option value="all">전체 상태</option>
              <option value="upcoming">예정</option>
              <option value="full">마감</option>
              <option value="ongoing">진행중</option>
              <option value="completed">종료</option>
            </select>
            <label className="flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-lg cursor-pointer hover:bg-stone-50 transition-colors" data-testid="show-hidden-samples-toggle">
              <input
                type="checkbox"
                checked={showHiddenSamples}
                onChange={(e) => setShowHiddenSamples(e.target.checked)}
                className="w-4 h-4 text-purple-500 focus:ring-purple-500 rounded"
              />
              <span className="text-sm text-stone-700">숨김 샘플 표시</span>
              {hiddenSampleIds.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-stone-200 text-stone-700">{hiddenSampleIds.length}</span>
              )}
            </label>
          </div>
        </div>

        {/* Events Table */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">이벤트</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">유형</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">일시</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">장소</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">참가비</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">참가자</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-500 uppercase">상태</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-500 uppercase">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredEvents.map((event) => (
                  <tr key={event.id} className={`hover:bg-stone-50 ${event.isHidden ? "opacity-60 bg-stone-50/50" : ""}`}>
                    <td className="px-4 py-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-stone-800">{event.title}</p>
                          {(() => {
                            const cat = eventCategoryOptions.find((o) => o.value === (event.eventCategory || "offline"));
                            if (!cat) return null;
                            return (
                              <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-purple-50 text-purple-700 border border-purple-200">
                                {cat.emoji} {cat.label}
                              </span>
                            );
                          })()}
                          {event.eligibility && event.eligibility !== "all" && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-burgundy-50 text-burgundy-700 border border-burgundy-200">
                              {eligibilityLabel(event.eligibility)}
                            </span>
                          )}
                          {event.isSample && (
                            <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded border ${event.isHidden ? "bg-stone-200 text-stone-600 border-stone-300" : "bg-amber-100 text-amber-700 border-amber-200"}`} title={event.isHidden ? "현재 이벤트 페이지에서 숨김 처리됨" : "이벤트 페이지에 예시로 노출되는 샘플 카드입니다"}>
                              {event.isHidden ? "샘플(숨김)" : "샘플"}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-stone-500 truncate max-w-[200px]">{event.description}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeLabels[event.type].color}`}>
                        {typeLabels[event.type].label}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1 text-sm text-stone-600">
                        <Clock className="w-3 h-3" />
                        <span>
                          {event.date}
                          {event.endDate && event.endDate !== event.date ? ` ~ ${event.endDate}` : ""}
                          {" "}{event.time}
                        </span>
                        <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-stone-100 text-stone-500">
                          {event.timezone === "America/New_York" ? "ET" : event.timezone === "America/Los_Angeles" ? "PT" : "KST"}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1 text-sm text-stone-600">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate max-w-[120px]">{event.location}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm font-medium text-stone-800">
                        {formatPrice(event.price, event.currency)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3 text-stone-400" />
                        {event.isSample ? (
                          <span className="text-sm text-stone-600">
                            {event.currentParticipants}/{event.maxParticipants}
                          </span>
                        ) : (
                          <div className="flex items-center gap-0.5 text-sm text-stone-600">
                            <input
                              type="number"
                              min="0"
                              value={
                                participantEdits[event.id]?.currentParticipants !== undefined
                                  ? participantEdits[event.id].currentParticipants
                                  : (event.currentParticipants ?? 0)
                              }
                              onChange={(e) =>
                                setParticipantEdits((prev) => ({
                                  ...prev,
                                  [event.id]: { ...(prev[event.id] || {}), currentParticipants: e.target.value },
                                }))
                              }
                              onFocus={(e) => e.target.select()}
                              onBlur={(e) => handleInlineParticipantsSave(event, "currentParticipants", e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                              className="w-14 px-2 py-1 border border-stone-200 rounded text-center text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                              title="참가 인원 (수동 입력)"
                              data-testid="inline-current-participants"
                            />
                            <span className="text-stone-400 mx-0.5">/</span>
                            <input
                              type="number"
                              min="1"
                              value={
                                participantEdits[event.id]?.maxParticipants !== undefined
                                  ? participantEdits[event.id].maxParticipants
                                  : (event.maxParticipants ?? 20)
                              }
                              onChange={(e) =>
                                setParticipantEdits((prev) => ({
                                  ...prev,
                                  [event.id]: { ...(prev[event.id] || {}), maxParticipants: e.target.value },
                                }))
                              }
                              onFocus={(e) => e.target.select()}
                              onBlur={(e) => handleInlineParticipantsSave(event, "maxParticipants", e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                              className="w-14 px-2 py-1 border border-stone-200 rounded text-center text-sm focus:outline-none focus:border-purple-400 focus:ring-1 focus:ring-purple-200"
                              title="모집 인원 (수동 입력)"
                              data-testid="inline-max-participants"
                            />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusLabels[event.effectiveStatus]?.color || 'bg-stone-100 text-stone-600'}`}>
                        {statusLabels[event.effectiveStatus]?.label || event.effectiveStatus}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          className="p-1.5 text-stone-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                          title="상세보기"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => !event.isSample && handleEditClick(event)}
                          disabled={event.isSample}
                          className={`p-1.5 rounded-lg transition-colors ${event.isSample ? "text-stone-300 cursor-not-allowed" : "text-stone-400 hover:text-green-500 hover:bg-green-50"}`}
                          title={event.isSample ? "샘플 이벤트는 수정할 수 없습니다" : "수정"}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {event.isSample ? (
                          <>
                            <button
                              onClick={() => handleCloneSampleToDB(event)}
                              className="p-1.5 rounded-lg transition-colors text-stone-400 hover:text-blue-600 hover:bg-blue-50"
                              title="실제 이벤트로 가져오기 (DB에 복제)"
                              data-testid="sample-clone-to-db-btn"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleToggleSampleHidden(event.id, !event.isHidden)}
                              className={`p-1.5 rounded-lg transition-colors ${event.isHidden ? "text-purple-500 hover:text-purple-700 hover:bg-purple-50" : "text-stone-400 hover:text-amber-500 hover:bg-amber-50"}`}
                              title={event.isHidden ? "복원하기 (이벤트 페이지에 다시 노출)" : "숨기기 (이벤트 페이지에서 제거)"}
                              data-testid="sample-hide-toggle-btn"
                            >
                              {event.isHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => handleDelete(event.id)}
                            className="p-1.5 rounded-lg transition-colors text-stone-400 hover:text-red-500 hover:bg-red-50"
                            title="삭제"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredEvents.length === 0 && (
            <div className="p-12 text-center">
              <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">이벤트가 없습니다</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-stone-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-stone-800">새 이벤트 생성</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5 rotate-45" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* 이벤트 분류 (공개 페이지 필터링) */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  이벤트 분류 <span className="text-red-500">*</span>
                  <span className="text-stone-400 text-xs ml-1">(공개 이벤트 페이지에서 이 분류로 노출됩니다)</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="event-category-options">
                  {eventCategoryOptions.map((opt) => {
                    const isActive = newEvent.eventCategory === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setNewEvent({ ...newEvent, eventCategory: opt.value })}
                        className={`px-3 py-2.5 rounded-xl border text-left transition-all ${
                          isActive
                            ? "bg-purple-50 border-purple-500 ring-2 ring-purple-200"
                            : "bg-white border-stone-200 hover:border-purple-300"
                        }`}
                        data-testid={`event-category-${opt.value}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg leading-none">{opt.emoji}</span>
                          <span className={`text-sm font-semibold ${isActive ? "text-purple-700" : "text-stone-800"}`}>{opt.label}</span>
                        </div>
                        <p className="text-[11px] text-stone-500 mt-1 leading-snug">{opt.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 이벤트 제목 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  이벤트 제목 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  placeholder="예: 4월 커플 소풍 이벤트"
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                />
              </div>

              {/* 참여 대상 (회원 등급) */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  참여 대상 <span className="text-red-500">*</span>
                  <span className="text-stone-400 text-xs ml-1">(어떤 회원이 참여할 수 있나요?)</span>
                </label>
                <div className="grid grid-cols-3 gap-2" data-testid="event-eligibility-options">
                  {eligibilityOptions.map((opt) => {
                    const isActive = (newEvent.eligibility || "all") === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setNewEvent({ ...newEvent, eligibility: opt.value })}
                        className={`px-3 py-2.5 rounded-xl border text-left transition-all ${
                          isActive
                            ? "bg-burgundy-50 border-burgundy-500 ring-2 ring-burgundy-200"
                            : "bg-white border-stone-200 hover:border-burgundy-300"
                        }`}
                        data-testid={`event-eligibility-${opt.value}`}
                      >
                        <span className={`text-sm font-semibold ${isActive ? "text-burgundy-700" : "text-stone-800"}`}>{opt.label}</span>
                        <p className="text-[11px] text-stone-500 mt-1 leading-snug">{opt.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 이벤트 유형 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  이벤트 유형 <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="eventType"
                      value="offline"
                      checked={newEvent.type === "offline"}
                      onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}
                      className="text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-stone-700">오프라인</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="eventType"
                      value="online"
                      checked={newEvent.type === "online"}
                      onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}
                      className="text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-stone-700">온라인 (Zoom)</span>
                  </label>
                </div>
              </div>

              {/* 날짜 기간 & 시간 */}
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    시작 날짜 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    종료 날짜 <span className="text-stone-400 text-xs">(선택, 1박 이상)</span>
                  </label>
                  <input
                    type="date"
                    value={newEvent.endDate}
                    min={newEvent.date || undefined}
                    onChange={(e) => setNewEvent({ ...newEvent, endDate: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                    data-testid="event-end-date"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    시작 시간 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={newEvent.time}
                    onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>
              </div>

              {/* 타임존 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  타임존 <span className="text-stone-400 text-xs">(이벤트 진행 지역 기준)</span>
                </label>
                <select
                  value={newEvent.timezone}
                  onChange={(e) => setNewEvent({ ...newEvent, timezone: e.target.value })}
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  data-testid="event-timezone-select"
                >
                  {timezoneOptions.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>

              {/* 장소 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  장소 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  placeholder={newEvent.type === "online" ? "Zoom 링크" : "예: 서울 강남구 테헤란로"}
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                />
              </div>

              {/* 참가비 & 통화 & 최대 인원 */}
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    통화
                  </label>
                  <select
                    value={newEvent.currency}
                    onChange={(e) => setNewEvent({ ...newEvent, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                    data-testid="event-currency-select"
                  >
                    {currencyOptions.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    참가비 ({currencySymbol(newEvent.currency)})
                  </label>
                  <input
                    type="number"
                    value={newEvent.price}
                    onChange={(e) => setNewEvent({ ...newEvent, price: e.target.value })}
                    placeholder={newEvent.currency === "USD" ? "49" : "50000"}
                    className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    최대 참가 인원
                  </label>
                  <input
                    type="number"
                    value={newEvent.maxParticipants}
                    onChange={(e) => setNewEvent({ ...newEvent, maxParticipants: e.target.value })}
                    placeholder="20"
                    className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  />
                </div>
              </div>

              {/* 설명 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  이벤트 설명
                </label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  placeholder="이벤트에 대한 상세 설명을 입력하세요..."
                  rows={3}
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none"
                />
              </div>

              {/* 이벤트 이미지 (AI 생성 or 업로드) */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  이벤트 이미지 <span className="text-stone-400 text-xs">(16:9 권장)</span>
                </label>
                <div className="inline-flex p-1 bg-stone-100 rounded-lg mb-3" data-testid="event-image-mode-tabs">
                  <button
                    type="button"
                    onClick={() => setImageMode("ai")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${imageMode === "ai" ? "bg-white text-purple-600 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
                    data-testid="image-mode-ai-btn"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    AI 자동 생성
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageMode("upload")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${imageMode === "upload" ? "bg-white text-purple-600 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
                    data-testid="image-mode-upload-btn"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    직접 업로드
                  </button>
                </div>

                {newEvent.image ? (
                  <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-stone-200 bg-stone-50">
                    <img
                      src={fullImageUrl(newEvent.image)}
                      alt="이벤트 미리보기"
                      className="w-full h-full object-cover"
                      onError={() => setNewEvent({ ...newEvent, image: null })}
                    />
                    <button
                      type="button"
                      onClick={() => setNewEvent({ ...newEvent, image: null })}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white"
                      title="이미지 제거"
                      data-testid="event-image-remove-btn"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : imageMode === "ai" ? (
                  <div className="space-y-2">
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="(선택) 직접 프롬프트 작성 — 예: '뉴욕 맨해튼 야경 배경, 와인잔을 든 40대 한인 커플이 미소짓는 따뜻한 분위기' (비워두면 제목+설명으로 자동 생성)"
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 resize-none"
                      data-testid="event-image-ai-prompt"
                    />
                    <button
                      type="button"
                      onClick={() => handleGenerateImage(newEvent, setNewEvent)}
                      disabled={imageBusy || (!newEvent.title && !aiPrompt.trim())}
                      className="w-full aspect-video rounded-lg border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 transition-all flex flex-col items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="event-image-generate-btn"
                    >
                      {imageBusy ? (
                        <>
                          <Loader2 className="w-7 h-7 text-purple-500 animate-spin" />
                          <p className="text-sm font-medium text-purple-700">AI가 이미지를 생성 중...</p>
                          <p className="text-xs text-purple-500">최대 30초까지 소요될 수 있어요</p>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-7 h-7 text-purple-500" />
                          <p className="text-sm font-medium text-purple-700">AI로 이벤트 이미지 생성</p>
                          <p className="text-xs text-stone-500">{aiPrompt.trim() ? "위 프롬프트 + 제목·설명·유형을 함께 반영합니다" : (newEvent.title ? "제목·설명·유형 기반으로 자동 생성" : "프롬프트나 이벤트 제목 중 하나는 필요해요")}</p>
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="event-image-upload-input"
                    className={`w-full aspect-video rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 hover:bg-stone-100 transition-all flex flex-col items-center justify-center gap-2 ${imageBusy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    {imageBusy ? (
                      <Loader2 className="w-7 h-7 text-stone-500 animate-spin" />
                    ) : (
                      <>
                        <ImageIcon className="w-7 h-7 text-stone-400" />
                        <p className="text-sm font-medium text-stone-700">클릭해서 이미지 파일 선택</p>
                        <p className="text-xs text-stone-500">JPG, PNG, WEBP · 최대 10MB</p>
                      </>
                    )}
                    <input
                      id="event-image-upload-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={imageBusy}
                      onChange={(e) => handleUploadImage(e.target.files?.[0], newEvent, setNewEvent)}
                      data-testid="event-image-upload-input"
                    />
                  </label>
                )}
              </div>

              {/* 공지 카테고리 선택 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  공지 카테고리 <span className="text-stone-400 text-xs">(어느 곳에 공지할지 선택 - 다중 선택 가능)</span>
                </label>
                <div className="max-h-[300px] overflow-y-auto border border-stone-200 rounded-lg p-3 space-y-4">
                  {categoryGroups.map((group) => (
                    <div key={group}>
                      <div className="text-xs font-semibold text-purple-600 uppercase mb-2 sticky top-0 bg-white py-1">
                        {group}
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {noticeCategories.filter(c => c.group === group).map((category) => {
                          const isSelected = newEvent.noticeCategories?.includes(category.id);
                          return (
                            <button
                              key={category.id}
                              type="button"
                              onClick={() => {
                                const current = newEvent.noticeCategories || [];
                                if (isSelected) {
                                  setNewEvent({
                                    ...newEvent,
                                    noticeCategories: current.filter(c => c !== category.id)
                                  });
                                } else {
                                  setNewEvent({
                                    ...newEvent,
                                    noticeCategories: [...current, category.id]
                                  });
                                }
                              }}
                              className={`px-2 py-1.5 rounded text-left text-xs transition-all border ${
                                isSelected
                                  ? "bg-purple-500 text-white border-purple-500"
                                  : "bg-white text-stone-600 border-stone-200 hover:border-purple-300 hover:bg-purple-50"
                              }`}
                            >
                              <div className="font-medium truncate">{category.label}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-stone-500">
                    선택됨: {newEvent.noticeCategories?.length || 0}개
                  </p>
                  {(!newEvent.noticeCategories || newEvent.noticeCategories.length === 0) && (
                    <p className="text-xs text-amber-600">
                      * 최소 1개 이상 선택 필요
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-stone-200 flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                disabled={submitting}
              >
                취소
              </Button>
              <Button
                onClick={handleCreateEvent}
                className="bg-purple-500 hover:bg-purple-600"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    생성 중...
                  </>
                ) : (
                  '이벤트 생성'
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && editingEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-stone-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-stone-800">이벤트 수정</h2>
                <button
                  onClick={() => { setShowEditModal(false); setEditingEvent(null); }}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5 rotate-45" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* 이벤트 분류 (공개 페이지 필터링) */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  이벤트 분류 <span className="text-red-500">*</span>
                  <span className="text-stone-400 text-xs ml-1">(공개 이벤트 페이지에서 이 분류로 노출됩니다)</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {eventCategoryOptions.map((opt) => {
                    const isActive = editingEvent.eventCategory === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setEditingEvent({ ...editingEvent, eventCategory: opt.value })}
                        className={`px-3 py-2.5 rounded-xl border text-left transition-all ${
                          isActive
                            ? "bg-purple-50 border-purple-500 ring-2 ring-purple-200"
                            : "bg-white border-stone-200 hover:border-purple-300"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg leading-none">{opt.emoji}</span>
                          <span className={`text-sm font-semibold ${isActive ? "text-purple-700" : "text-stone-800"}`}>{opt.label}</span>
                        </div>
                        <p className="text-[11px] text-stone-500 mt-1 leading-snug">{opt.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 이벤트 제목 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  이벤트 제목 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingEvent.title}
                  onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                />
              </div>

              {/* 참여 대상 (회원 등급) */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  참여 대상 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2" data-testid="edit-event-eligibility-options">
                  {eligibilityOptions.map((opt) => {
                    const isActive = (editingEvent.eligibility || "all") === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setEditingEvent({ ...editingEvent, eligibility: opt.value })}
                        className={`px-3 py-2.5 rounded-xl border text-left transition-all ${
                          isActive
                            ? "bg-burgundy-50 border-burgundy-500 ring-2 ring-burgundy-200"
                            : "bg-white border-stone-200 hover:border-burgundy-300"
                        }`}
                        data-testid={`edit-event-eligibility-${opt.value}`}
                      >
                        <span className={`text-sm font-semibold ${isActive ? "text-burgundy-700" : "text-stone-800"}`}>{opt.label}</span>
                        <p className="text-[11px] text-stone-500 mt-1 leading-snug">{opt.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 이벤트 유형 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  이벤트 유형
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="offline"
                      checked={editingEvent.type === "offline"}
                      onChange={(e) => setEditingEvent({ ...editingEvent, type: e.target.value })}
                      className="text-purple-500"
                    />
                    <span className="text-stone-700">오프라인</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="online"
                      checked={editingEvent.type === "online"}
                      onChange={(e) => setEditingEvent({ ...editingEvent, type: e.target.value })}
                      className="text-purple-500"
                    />
                    <span className="text-stone-700">온라인 (Zoom)</span>
                  </label>
                </div>
              </div>

              {/* 날짜 기간 & 시간 */}
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">시작 날짜</label>
                  <input
                    type="date"
                    value={editingEvent.date}
                    onChange={(e) => setEditingEvent({ ...editingEvent, date: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    종료 날짜 <span className="text-stone-400 text-xs">(선택)</span>
                  </label>
                  <input
                    type="date"
                    value={editingEvent.endDate || ""}
                    min={editingEvent.date || undefined}
                    onChange={(e) => setEditingEvent({ ...editingEvent, endDate: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">시작 시간</label>
                  <input
                    type="time"
                    value={editingEvent.time}
                    onChange={(e) => setEditingEvent({ ...editingEvent, time: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  />
                </div>
              </div>

              {/* 타임존 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  타임존 <span className="text-stone-400 text-xs">(이벤트 진행 지역 기준)</span>
                </label>
                <select
                  value={editingEvent.timezone || "Asia/Seoul"}
                  onChange={(e) => setEditingEvent({ ...editingEvent, timezone: e.target.value })}
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                >
                  {timezoneOptions.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>

              {/* 장소 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">장소</label>
                <input
                  type="text"
                  value={editingEvent.location}
                  onChange={(e) => setEditingEvent({ ...editingEvent, location: e.target.value })}
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                />
              </div>

              {/* 참가비 & 통화 & 최대 인원 */}
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">통화</label>
                  <select
                    value={editingEvent.currency || "KRW"}
                    onChange={(e) => setEditingEvent({ ...editingEvent, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  >
                    {currencyOptions.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    참가비 ({currencySymbol(editingEvent.currency || "KRW")})
                  </label>
                  <input
                    type="number"
                    value={editingEvent.price}
                    onChange={(e) => setEditingEvent({ ...editingEvent, price: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  />
                </div>
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-stone-700 mb-1">최대 인원</label>
                  <input
                    type="number"
                    value={editingEvent.maxParticipants}
                    onChange={(e) => setEditingEvent({ ...editingEvent, maxParticipants: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  />
                </div>
              </div>

              {/* 현재 참가자 수 (관리자 수동 보정 — 종료된 행사도 수정 가능) */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  현재 참가자 수
                  <span className="ml-1 text-xs text-stone-400">(종료된 이벤트의 실제 참석자 수 수동 보정용)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  value={editingEvent.currentParticipants ?? 0}
                  onChange={(e) => setEditingEvent({ ...editingEvent, currentParticipants: e.target.value })}
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  data-testid="event-current-participants-input"
                />
              </div>

              {/* 상태 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">이벤트 상태</label>
                <select
                  value={editingEvent.status}
                  onChange={(e) => setEditingEvent({ ...editingEvent, status: e.target.value })}
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                >
                  <option value="upcoming">예정</option>
                  <option value="ongoing">진행중</option>
                  <option value="full">마감</option>
                  <option value="completed">종료</option>
                </select>
              </div>

              {/* 설명 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">설명</label>
                <textarea
                  value={editingEvent.description}
                  onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none"
                />
              </div>

              {/* 이벤트 이미지 (편집) */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  이벤트 이미지 <span className="text-stone-400 text-xs">(16:9 권장)</span>
                </label>
                <div className="inline-flex p-1 bg-stone-100 rounded-lg mb-3">
                  <button
                    type="button"
                    onClick={() => setImageMode("ai")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${imageMode === "ai" ? "bg-white text-purple-600 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    AI 자동 생성
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageMode("upload")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${imageMode === "upload" ? "bg-white text-purple-600 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
                  >
                    <Upload className="w-3.5 h-3.5" />
                    직접 업로드
                  </button>
                </div>

                {editingEvent.image ? (
                  <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-stone-200 bg-stone-50">
                    <img
                      src={fullImageUrl(editingEvent.image)}
                      alt="이벤트 미리보기"
                      className="w-full h-full object-cover"
                      onError={() => setEditingEvent({ ...editingEvent, image: null })}
                    />
                    <button
                      type="button"
                      onClick={() => setEditingEvent({ ...editingEvent, image: null })}
                      className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white"
                      title="이미지 제거"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : imageMode === "ai" ? (
                  <div className="space-y-2">
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="(선택) 직접 프롬프트 작성 — 비워두면 제목+설명으로 자동 생성"
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 resize-none"
                    />
                    <button
                      type="button"
                      onClick={() => handleGenerateImage(editingEvent, setEditingEvent)}
                      disabled={imageBusy || (!editingEvent.title && !aiPrompt.trim())}
                      className="w-full aspect-video rounded-lg border-2 border-dashed border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 transition-all flex flex-col items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {imageBusy ? (
                        <>
                          <Loader2 className="w-7 h-7 text-purple-500 animate-spin" />
                          <p className="text-sm font-medium text-purple-700">AI가 이미지를 생성 중...</p>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-7 h-7 text-purple-500" />
                          <p className="text-sm font-medium text-purple-700">AI로 이벤트 이미지 생성</p>
                          <p className="text-xs text-stone-500">{aiPrompt.trim() ? "프롬프트 + 이벤트 정보 함께 반영" : "제목·설명·유형 기반 자동 생성"}</p>
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="event-image-edit-upload-input"
                    className={`w-full aspect-video rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 hover:bg-stone-100 transition-all flex flex-col items-center justify-center gap-2 ${imageBusy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    {imageBusy ? (
                      <Loader2 className="w-7 h-7 text-stone-500 animate-spin" />
                    ) : (
                      <>
                        <ImageIcon className="w-7 h-7 text-stone-400" />
                        <p className="text-sm font-medium text-stone-700">클릭해서 이미지 파일 선택</p>
                        <p className="text-xs text-stone-500">JPG, PNG, WEBP · 최대 10MB</p>
                      </>
                    )}
                    <input
                      id="event-image-edit-upload-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={imageBusy}
                      onChange={(e) => handleUploadImage(e.target.files?.[0], editingEvent, setEditingEvent)}
                    />
                  </label>
                )}
              </div>

              {/* 공지 카테고리 */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">공지 카테고리</label>
                <div className="max-h-[250px] overflow-y-auto border border-stone-200 rounded-lg p-3 space-y-4">
                  {categoryGroups.map((group) => (
                    <div key={group}>
                      <div className="text-xs font-semibold text-purple-600 uppercase mb-2 sticky top-0 bg-white py-1">
                        {group}
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {noticeCategories.filter(c => c.group === group).map((category) => {
                          const isSelected = editingEvent.noticeCategories?.includes(category.id);
                          return (
                            <button
                              key={category.id}
                              type="button"
                              onClick={() => {
                                const current = editingEvent.noticeCategories || [];
                                if (isSelected) {
                                  setEditingEvent({
                                    ...editingEvent,
                                    noticeCategories: current.filter(c => c !== category.id)
                                  });
                                } else {
                                  setEditingEvent({
                                    ...editingEvent,
                                    noticeCategories: [...current, category.id]
                                  });
                                }
                              }}
                              className={`px-2 py-1.5 rounded text-left text-xs transition-all border ${
                                isSelected
                                  ? "bg-purple-500 text-white border-purple-500"
                                  : "bg-white text-stone-600 border-stone-200 hover:border-purple-300"
                              }`}
                            >
                              <div className="font-medium truncate">{category.label}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-stone-500">
                  선택됨: {editingEvent.noticeCategories?.length || 0}개
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-stone-200 flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => { setShowEditModal(false); setEditingEvent(null); }}
                disabled={submitting}
              >
                취소
              </Button>
              <Button
                onClick={handleUpdateEvent}
                className="bg-green-500 hover:bg-green-600"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    저장 중...
                  </>
                ) : (
                  '변경사항 저장'
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
